import { AutoFixHigh, Check, PlayArrow, Warning } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AxiosError } from 'axios';
import { useFormik } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { dynamicAdapterConfigsApi } from 'api';
import { useMutation, usePrevious, useProviderTypes } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  ConfigValidationResult,
  CreateDynamicAdapterConfigDto,
  DynamicAdapterConfig,
  DynamicPayinConfig,
  DynamicPayoutConfig,
  MappingsConfig,
  StatusMappingConfig,
} from 'types';
import { validationUtils } from 'utils';

import { FullMappingsEditor } from './components';
import { useDynamicAdapterConfigs } from './hooks/useDynamicAdapterConfigs';

type Props = {
  open: boolean;
  data?: DynamicAdapterConfig;
  onClose: () => void;
};

type TabValue =
  | 'general'
  | 'payin'
  | 'payinMappings'
  | 'payout'
  | 'payoutMappings'
  | 'help';

type FormValues = {
  name: string;
  providerType: string;
  version: string;
  payinConfigJson: string;
  payoutConfigJson: string;
};

const KEYS = {
  create_title: 'pages.dynamic_adapter_configs.dialog.create_title',
  edit_title: 'pages.dynamic_adapter_configs.dialog.edit_title',
  tabs: {
    general: 'pages.dynamic_adapter_configs.dialog.tabs.general',
    payin: 'pages.dynamic_adapter_configs.dialog.tabs.payin',
    payinMappings: 'pages.dynamic_adapter_configs.dialog.tabs.payin_mappings',
    payout: 'pages.dynamic_adapter_configs.dialog.tabs.payout',
    payoutMappings: 'pages.dynamic_adapter_configs.dialog.tabs.payout_mappings',
    help: 'pages.dynamic_adapter_configs.dialog.tabs.help',
  },
  fields: {
    name: 'pages.dynamic_adapter_configs.dialog.fields.name',
    provider_type: 'pages.dynamic_adapter_configs.dialog.fields.provider_type',
    version: 'pages.dynamic_adapter_configs.dialog.fields.version',
    payin_config: 'pages.dynamic_adapter_configs.dialog.fields.payin_config',
    payout_config: 'pages.dynamic_adapter_configs.dialog.fields.payout_config',
  },
  placeholders: {
    payin_config:
      'pages.dynamic_adapter_configs.dialog.placeholders.payin_config',
    payout_config:
      'pages.dynamic_adapter_configs.dialog.placeholders.payout_config',
  },
  actions: {
    beautify: 'pages.dynamic_adapter_configs.dialog.actions.beautify',
    validate: 'pages.dynamic_adapter_configs.dialog.actions.validate',
  },
  errors: {
    invalid_json: 'pages.dynamic_adapter_configs.dialog.errors.invalid_json',
  },
  validation: {
    success: 'pages.dynamic_adapter_configs.dialog.validation.success',
    errors: 'pages.dynamic_adapter_configs.dialog.validation.errors',
    warnings: 'pages.dynamic_adapter_configs.dialog.validation.warnings',
  },
  help: {
    title: 'pages.dynamic_adapter_configs.dialog.help.title',
    structure_title:
      'pages.dynamic_adapter_configs.dialog.help.structure_title',
    structure_description:
      'pages.dynamic_adapter_configs.dialog.help.structure_description',
    context_title: 'pages.dynamic_adapter_configs.dialog.help.context_title',
    context_description:
      'pages.dynamic_adapter_configs.dialog.help.context_description',
    endpoints_title:
      'pages.dynamic_adapter_configs.dialog.help.endpoints_title',
    endpoints_description:
      'pages.dynamic_adapter_configs.dialog.help.endpoints_description',
    transform_rules_title:
      'pages.dynamic_adapter_configs.dialog.help.transform_rules_title',
    transform_rules_description:
      'pages.dynamic_adapter_configs.dialog.help.transform_rules_description',
    auth_title: 'pages.dynamic_adapter_configs.dialog.help.auth_title',
    auth_description:
      'pages.dynamic_adapter_configs.dialog.help.auth_description',
    body_wrap_title:
      'pages.dynamic_adapter_configs.dialog.help.body_wrap_title',
    body_wrap_description:
      'pages.dynamic_adapter_configs.dialog.help.body_wrap_description',
    jsonpath_title: 'pages.dynamic_adapter_configs.dialog.help.jsonpath_title',
    jsonpath_description:
      'pages.dynamic_adapter_configs.dialog.help.jsonpath_description',
    template_title: 'pages.dynamic_adapter_configs.dialog.help.template_title',
    template_description:
      'pages.dynamic_adapter_configs.dialog.help.template_description',
    transforms_title:
      'pages.dynamic_adapter_configs.dialog.help.transforms_title',
    transforms_description:
      'pages.dynamic_adapter_configs.dialog.help.transforms_description',
    success_condition_title:
      'pages.dynamic_adapter_configs.dialog.help.success_condition_title',
    success_condition_description:
      'pages.dynamic_adapter_configs.dialog.help.success_condition_description',
    status_mappings_title:
      'pages.dynamic_adapter_configs.dialog.help.status_mappings_title',
    status_mappings_description:
      'pages.dynamic_adapter_configs.dialog.help.status_mappings_description',
    mappings_title: 'pages.dynamic_adapter_configs.dialog.help.mappings_title',
    mappings_description:
      'pages.dynamic_adapter_configs.dialog.help.mappings_description',
    webhook_title: 'pages.dynamic_adapter_configs.dialog.help.webhook_title',
    webhook_description:
      'pages.dynamic_adapter_configs.dialog.help.webhook_description',
    required_credentials_title:
      'pages.dynamic_adapter_configs.dialog.help.required_credentials_title',
    required_credentials_description:
      'pages.dynamic_adapter_configs.dialog.help.required_credentials_description',
    complete_example_title:
      'pages.dynamic_adapter_configs.dialog.help.complete_example_title',
    complete_example_description:
      'pages.dynamic_adapter_configs.dialog.help.complete_example_description',
    docs_link: 'pages.dynamic_adapter_configs.dialog.help.docs_link',
    fernet_link: 'pages.dynamic_adapter_configs.dialog.help.fernet_link',
  },
} as const;

const CODE_BLOCK_SX = {
  bgcolor: 'grey.100',
  p: 2,
  borderRadius: 1,
  fontFamily: 'monospace',
  fontSize: '0.75rem',
  overflow: 'auto',
  whiteSpace: 'pre' as const,
};

const HelpSection: React.FC<{
  title: string;
  description: string;
  code: string;
  link?: { href: string; label: string };
}> = ({ title, description, code, link }) => (
  <Box>
    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
      {title}
    </Typography>
    <Typography
      variant="body2"
      color="text.secondary"
      paragraph
      sx={{ whiteSpace: 'pre-line' }}
    >
      {description}
    </Typography>
    <Typography variant="body2" component="pre" sx={CODE_BLOCK_SX}>
      {code}
    </Typography>
    {link && (
      <Link
        href={link.href}
        target="_blank"
        rel="noopener"
        sx={{ mt: 1, display: 'inline-block' }}
      >
        {link.label}
      </Link>
    )}
  </Box>
);

export const DynamicAdapterConfigDetailsDialog: React.FC<Props> = ({
  open,
  data,
  onClose,
}) => {
  const { t: tAdmin } = useTranslation(TranslationNamespace.Admin);
  const t = tAdmin as unknown as (key: string) => string;
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const prevOpen = usePrevious(open);
  const isNew = !data?.id;

  const [activeTab, setActiveTab] = useState<TabValue>('general');
  const [validationResult, setValidationResult] =
    useState<ConfigValidationResult | null>(null);

  const { createMutation, updateMutation } = useDynamicAdapterConfigs();
  const { providerTypeOptions } = useProviderTypes();

  const validateJsonMutation = useMutation<
    ConfigValidationResult,
    AxiosError,
    { payinConfig?: any; payoutConfig?: any }
  >(dynamicAdapterConfigsApi.validateJson, {
    notifierType: 'none',
  });

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().required(tCommon('errors.required')),
        providerType: Yup.string().required(tCommon('errors.required')),
        version: Yup.string().required(tCommon('errors.required')),
        payinConfigJson: Yup.string().test(
          'valid-json',
          t(KEYS.errors.invalid_json),
          (value) => {
            if (!value) {
              return true;
            }
            try {
              JSON.parse(value);
              return true;
            } catch {
              return false;
            }
          },
        ),
        payoutConfigJson: Yup.string().test(
          'valid-json',
          t(KEYS.errors.invalid_json),
          (value) => {
            if (!value) {
              return true;
            }
            try {
              JSON.parse(value);
              return true;
            } catch {
              return false;
            }
          },
        ),
      }),
    [tCommon, t],
  );

  const formik = useFormik<FormValues>({
    initialValues: {
      name: '',
      providerType: '',
      version: '1.0.0',
      payinConfigJson: '',
      payoutConfigJson: '',
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values, helpers) => {
      try {
        const rawPayin = values.payinConfigJson
          ? JSON.parse(values.payinConfigJson)
          : undefined;
        const rawPayout = values.payoutConfigJson
          ? JSON.parse(values.payoutConfigJson)
          : undefined;

        const payload: CreateDynamicAdapterConfigDto = {
          name: values.name,
          providerType: values.providerType,
          version: values.version,
          payinConfig: rawPayin?.payin ?? rawPayin,
          payoutConfig: rawPayout?.payout ?? rawPayout,
        };

        if (isNew) {
          await createMutation.mutateAsync(payload);
        } else {
          await updateMutation.mutateAsync({ id: data!.id, data: payload });
        }
        onClose();
      } catch (error) {
        if (error instanceof AxiosError) {
          helpers.setErrors(validationUtils.getFormErrors(error));
        }
      }
    },
  });

  const handleBeautify = useCallback(
    (field: 'payinConfigJson' | 'payoutConfigJson') => {
      const value = formik.values[field];
      if (!value) {
        return;
      }
      try {
        const parsed = JSON.parse(value);
        formik.setFieldValue(field, JSON.stringify(parsed, null, 2));
      } catch {
        // Invalid JSON, can't beautify
      }
    },
    [formik],
  );

  // Helper to safely parse JSON config
  const parseConfig = useCallback(
    (jsonString: string): DynamicPayinConfig | DynamicPayoutConfig | null => {
      if (!jsonString) {
        return null;
      }
      try {
        return JSON.parse(jsonString);
      } catch {
        return null;
      }
    },
    [],
  );

  // Get parsed payin config for mappings editor
  // Unwrap if full adapter config was passed (with nested "payin" key)
  const payinConfig = useMemo(() => {
    const parsed = parseConfig(formik.values.payinConfigJson);
    if (!parsed) {
      return null;
    }
    return ((parsed as any).payin ?? parsed) as DynamicPayinConfig;
  }, [formik.values.payinConfigJson, parseConfig]);

  // Get parsed payout config for mappings editor
  // Unwrap if full adapter config was passed (with nested "payout" key)
  const payoutConfig = useMemo(() => {
    const parsed = parseConfig(formik.values.payoutConfigJson);
    if (!parsed) {
      return null;
    }
    return ((parsed as any).payout ?? parsed) as DynamicPayoutConfig;
  }, [formik.values.payoutConfigJson, parseConfig]);

  // Handler to update payin mappings
  const handlePayinMappingsChange = useCallback(
    (mappings: MappingsConfig) => {
      const config = payinConfig || ({} as DynamicPayinConfig);
      const updated = { ...config, mappings };
      formik.setFieldValue('payinConfigJson', JSON.stringify(updated, null, 2));
    },
    [payinConfig, formik],
  );

  // Handler to update payin status mappings
  const handlePayinStatusMappingsChange = useCallback(
    (statusMappings: StatusMappingConfig) => {
      const config = payinConfig || ({} as DynamicPayinConfig);
      const updated = { ...config, statusMappings };
      formik.setFieldValue('payinConfigJson', JSON.stringify(updated, null, 2));
    },
    [payinConfig, formik],
  );

  // Handler to update payout mappings
  const handlePayoutMappingsChange = useCallback(
    (mappings: MappingsConfig) => {
      const config = payoutConfig || ({} as DynamicPayoutConfig);
      const updated = { ...config, mappings };
      formik.setFieldValue(
        'payoutConfigJson',
        JSON.stringify(updated, null, 2),
      );
    },
    [payoutConfig, formik],
  );

  // Handler to update payout status mappings
  const handlePayoutStatusMappingsChange = useCallback(
    (statusMappings: StatusMappingConfig) => {
      const config = payoutConfig || ({} as DynamicPayoutConfig);
      const updated = { ...config, statusMappings };
      formik.setFieldValue(
        'payoutConfigJson',
        JSON.stringify(updated, null, 2),
      );
    },
    [payoutConfig, formik],
  );

  const handleValidate = useCallback(async () => {
    try {
      const payinConfig = formik.values.payinConfigJson
        ? JSON.parse(formik.values.payinConfigJson)
        : undefined;
      const payoutConfig = formik.values.payoutConfigJson
        ? JSON.parse(formik.values.payoutConfigJson)
        : undefined;

      const result = await validateJsonMutation.mutateAsync({
        payinConfig,
        payoutConfig,
      });
      setValidationResult(result);
    } catch {
      // JSON parse error handled by form validation
    }
  }, [formik.values, validateJsonMutation]);

  useEffect(() => {
    if (open && !prevOpen) {
      setActiveTab('general');
      setValidationResult(null);
      if (data) {
        formik.setValues({
          name: data.name,
          providerType: data.providerType,
          version: data.version,
          payinConfigJson: data.payinConfig
            ? JSON.stringify(data.payinConfig, null, 2)
            : '',
          payoutConfigJson: data.payoutConfig
            ? JSON.stringify(data.payoutConfig, null, 2)
            : '',
        });
      } else {
        formik.resetForm();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prevOpen, data]);

  const handleClose = useCallback(() => {
    formik.resetForm();
    onClose();
  }, [formik, onClose]);

  const title = isNew ? t(KEYS.create_title) : t(KEYS.edit_title);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ px: 6, pt: 4, pb: 2 }}>{title}</DialogTitle>
      <DialogContent sx={{ px: 6, pt: 0, pb: 3 }}>
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              aria-label="config tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label={t(KEYS.tabs.general)} value="general" />
              <Tab label={t(KEYS.tabs.payin)} value="payin" />
              <Tab label={t(KEYS.tabs.payinMappings)} value="payinMappings" />
              <Tab label={t(KEYS.tabs.payout)} value="payout" />
              <Tab label={t(KEYS.tabs.payoutMappings)} value="payoutMappings" />
              <Tab label={t(KEYS.tabs.help)} value="help" />
            </Tabs>
          </Box>

          {activeTab === 'general' && (
            <Stack spacing={3} sx={{ pt: 3 }}>
              <TextField
                fullWidth
                label={t(KEYS.fields.name)}
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && !!formik.errors.name}
                helperText={formik.touched.name && formik.errors.name}
              />

              <FormControl
                fullWidth
                error={
                  formik.touched.providerType && !!formik.errors.providerType
                }
              >
                <InputLabel>{t(KEYS.fields.provider_type)}</InputLabel>
                <Select
                  name="providerType"
                  value={formik.values.providerType}
                  label={t(KEYS.fields.provider_type)}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  {providerTypeOptions.map(
                    (option: { value: string; label: string }) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ),
                  )}
                </Select>
                {formik.touched.providerType && formik.errors.providerType && (
                  <FormHelperText>{formik.errors.providerType}</FormHelperText>
                )}
              </FormControl>

              <TextField
                fullWidth
                label={t(KEYS.fields.version)}
                name="version"
                value={formik.values.version}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.version && !!formik.errors.version}
                helperText={formik.touched.version && formik.errors.version}
              />
            </Stack>
          )}

          {activeTab === 'payin' && (
            <Stack spacing={2} sx={{ pt: 3 }}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Tooltip title={t(KEYS.actions.beautify)}>
                  <IconButton
                    size="small"
                    onClick={() => handleBeautify('payinConfigJson')}
                  >
                    <AutoFixHigh />
                  </IconButton>
                </Tooltip>
              </Stack>
              <TextField
                fullWidth
                multiline
                rows={20}
                label={t(KEYS.fields.payin_config)}
                name="payinConfigJson"
                value={formik.values.payinConfigJson}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.payinConfigJson &&
                  !!formik.errors.payinConfigJson
                }
                helperText={
                  formik.touched.payinConfigJson &&
                  formik.errors.payinConfigJson
                }
                placeholder={t(KEYS.placeholders.payin_config)}
                InputProps={{
                  sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
                }}
              />
            </Stack>
          )}

          {activeTab === 'payout' && (
            <Stack spacing={2} sx={{ pt: 3 }}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Tooltip title={t(KEYS.actions.beautify)}>
                  <IconButton
                    size="small"
                    onClick={() => handleBeautify('payoutConfigJson')}
                  >
                    <AutoFixHigh />
                  </IconButton>
                </Tooltip>
              </Stack>
              <TextField
                fullWidth
                multiline
                rows={20}
                label={t(KEYS.fields.payout_config)}
                name="payoutConfigJson"
                value={formik.values.payoutConfigJson}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.payoutConfigJson &&
                  !!formik.errors.payoutConfigJson
                }
                helperText={
                  formik.touched.payoutConfigJson &&
                  formik.errors.payoutConfigJson
                }
                placeholder={t(KEYS.placeholders.payout_config)}
                InputProps={{
                  sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
                }}
              />
            </Stack>
          )}

          {activeTab === 'payinMappings' && (
            <Box sx={{ pt: 3 }}>
              <FullMappingsEditor
                mappings={payinConfig?.mappings}
                statusMappings={payinConfig?.statusMappings}
                onMappingsChange={handlePayinMappingsChange}
                onStatusMappingsChange={handlePayinStatusMappingsChange}
              />
            </Box>
          )}

          {activeTab === 'payoutMappings' && (
            <Box sx={{ pt: 3 }}>
              <FullMappingsEditor
                mappings={payoutConfig?.mappings}
                statusMappings={payoutConfig?.statusMappings}
                onMappingsChange={handlePayoutMappingsChange}
                onStatusMappingsChange={handlePayoutStatusMappingsChange}
              />
            </Box>
          )}

          {activeTab === 'help' && (
            <Stack spacing={3} sx={{ pt: 2 }}>
              <Typography variant="h6">{t(KEYS.help.title)}</Typography>

              <Divider />

              <HelpSection
                title={t(KEYS.help.structure_title)}
                description={t(KEYS.help.structure_description)}
                code={`{
  "name": "myprovider-dynamic",
  "providerType": "myprovider",
  "baseUrl": "\${provider.payinConfig.url}",
  "payin": {
    "endpoints": { "create": { ... }, "get": { ... }, "cancel": { ... } },
    "auth": { ... },
    "statusMappings": { ... },
    "mappings": { ... },
    "webhook": { ... },
    "requiredCredentials": ["apiKey"]
  },
  "payout": { /* same shape, no "cancel" */ }
}`}
              />

              <HelpSection
                title={t(KEYS.help.context_title)}
                description={t(KEYS.help.context_description)}
                code={`// Available in every \${...} / $.path / condition
{
  order:           OrderEntity | PayoutOrderEntity,
  createOrderDto:  any,            // payin create only
  provider:        P2PProviderEntity,
  credentials:     Record<string, any>,
  providerOrder:   P2PProviderOrderEntity,  // get / cancel
  externalOrderId: string,                  // get / cancel
  mappings:        { banks, paymentTypes }, // + statusMappings in responseTransform
  response:        any,                     // responseTransform only
  callbackUrl:     string,
  requestBody:     any,                     // auth strategy only
  requestBodyJson: string,                  // auth strategy only
  timestamp:       string,                  // epoch seconds  (per-request, auth strategy)
  timestampMs:     string,                  // epoch ms       (per-request, auth strategy)
  timestampIso:    string,                  // ISO 8601 UTC   (per-request, auth strategy)
  idempotencyKey:  string                   // create / cancel with "idempotencyKey" (auth strategy)
}`}
              />

              <HelpSection
                title={t(KEYS.help.endpoints_title)}
                description={t(KEYS.help.endpoints_description)}
                code={`"endpoints": {
  "create": {
    "path": "/api/v2/orders",                  // appended to provider URL
    "method": "POST",                          // GET | POST | PUT | PATCH | DELETE
    "requestTransform":  [ /* TransformRule[] */ ],
    "responseTransform": [ /* TransformRule[] */ ],
    "successCondition": "$.status === 'success' && $.data.id",
    "bodyWrap": { ... },                       // optional, see below
    "idempotencyKey": {}                       // optional, create / cancel only: Idempotency-Key header
  },
  "get":    { "path": "/api/v2/orders/info?id=\${externalOrderId}", "method": "GET",  ... },
  "cancel": { "path": "/api/v2/orders/cancel", "method": "PUT", ... }   // payin only
}

// "idempotencyKey": { "header": "Idempotency-Key" }   // header is optional, this is the default
// Value = UUID derived from provider id + order id + operation:
// a resend of the same create / cancel carries the same key, create and cancel differ.`}
              />

              <HelpSection
                title={t(KEYS.help.transform_rules_title)}
                description={t(KEYS.help.transform_rules_description)}
                code={`// Each rule writes one field on the output object.
// "target" supports dot-notation for nesting (e.g. "requisites.bank").
// Pick ONE source: source | template | value.
{
  "target":    "externalOrderId",      // required
  "source":    "$.response.data.id",   // JSONPath read from context
  "template":  "order-\${order.id}",    // OR template string
  "value":     "fixed",                 // OR literal
  "mapping":   "banks.toProvider",     // optional: apply mappings.banks.toProvider[value]
  "transform": "toString",              // optional: toUpperCase|toLowerCase|toNumber|toString|toBoolean
  "condition": "$.createOrderDto.payment.bank",  // optional: only include if truthy
  "required":  false                    // optional: drop rule if value undefined
}`}
              />

              <HelpSection
                title={t(KEYS.help.auth_title)}
                description={t(KEYS.help.auth_description)}
                code={`// type: "header"
"auth": {
  "type": "header",
  "headers": [
    { "name": "Content-Type",  "value": "application/json" },
    { "name": "Authorization", "value": "Bearer \${credentials.apiKey}" }
  ]
}

// type: "basic" — HTTP Basic, Axios builds the Authorization header
"auth": {
  "type": "basic",
  "basic": {
    "usernamePath": "$.credentials.username",   // optional, default $.credentials.username
    "passwordPath": "$.credentials.password"    // optional, default $.credentials.password
  }
}

// type: "signature" — HMAC / hash, position = header (default) | query
"auth": {
  "type": "signature",
  "signature": {
    "algorithm":  "hmac-sha256",       // sha1 | sha256 | md5 | hmac-sha256 | hmac-sha512
    "template":   "\${timestamp}:\${requestBodyJson}",
    "position":   "header",
    "headerName": "X-Signature",
    "timestampHeaderName": "X-Timestamp" // optional: copies \${timestamp} into header
  }
}

// type: "signature" with query position
"auth": {
  "type": "signature",
  "signature": {
    "algorithm":      "sha256",
    "template":       "\${credentials.secretKey}:\${order.id}",
    "position":       "query",
    "queryParamName": "sign"
  }
}

// type: "http_signature" — RFC 9421 HTTP Message Signatures, Ed25519
// Signs the final URL, headers (incl. Idempotency-Key) and exact body bytes;
// adds Content-Digest (RFC 9530, when there is a body), Signature-Input and Signature.
"auth": {
  "type": "http_signature",
  "headers": [{ "name": "X-Api-Key", "value": "\${credentials.apiKey}" }],
  "httpSignature": {
    "algorithm":  "ed25519",
    "keyId":      "\${credentials.keyId}",
    "privateKey": "\${credentials.privateKey}",   // PKCS#8 PEM, or base64/hex 32-byte seed / 64-byte libsodium key
    "label":      "sig1",                          // optional, default sig1
    "components": ["@method", "@target-uri", "content-type", "content-digest", "idempotency-key"],
                                                   // optional, this is the default; absent ones are skipped
    "contentDigest": "sha-256",                    // optional: sha-256 | sha-512
    "includeAlg": true,                            // optional: alg="ed25519" param
    "expiresInSeconds": 60,                        // optional: expires param
    "includeNonce": false,                         // optional: nonce param
    "tag": "..."                                   // optional: tag param
  }
}
// Signature-Input: sig1=("@method" "@target-uri" ...);created=1791895942;keyid="...";alg="ed25519"
// Signature:       sig1=:<base64>:
// Derived components: @method @target-uri @authority @scheme @request-target @path @query`}
              />

              <HelpSection
                title={t(KEYS.help.body_wrap_title)}
                description={t(KEYS.help.body_wrap_description)}
                code={`// Runs AFTER requestTransform, BEFORE the HTTP send.
// Wraps the whole body { ... } into { [field]: <fernet-token> }.
"bodyWrap": {
  "type":    "fernet",
  "keyPath": "$.credentials.fernetKey",   // url-safe base64 32-byte key
  "field":   "encrypted_application"      // wrapper field in final HTTP body
}`}
                link={{
                  href: 'https://github.com/fernet/spec/blob/master/Spec.md',
                  label: t(KEYS.help.fernet_link),
                }}
              />

              <HelpSection
                title={t(KEYS.help.jsonpath_title)}
                description={t(KEYS.help.jsonpath_description)}
                code={`$.response.data.id      // simple path  (lodash.get)
$.items[0].name         // array index  (lodash.get)
$.items[*].id           // all ids      (jsonpath lib)
$..status               // recursive    (jsonpath lib)`}
                link={{
                  href: 'https://goessner.net/articles/JsonPath/',
                  label: t(KEYS.help.docs_link),
                }}
              />

              <HelpSection
                title={t(KEYS.help.template_title)}
                description={t(KEYS.help.template_description)}
                code={`\${order.id}                    // internal order ID
\${order.amount}                // order amount
\${createOrderDto.customerId}   // payin DTO field
\${credentials.apiKey}          // provider credential
\${provider.payinConfig.url}    // provider base URL
\${externalOrderId}             // get / cancel
\${callbackUrl}                 // simple-pay webhook URL for this provider`}
              />

              <HelpSection
                title={t(KEYS.help.transforms_title)}
                description={t(KEYS.help.transforms_description)}
                code={`toUpperCase    // "hello" -> "HELLO"
toLowerCase    // "HELLO" -> "hello"
toNumber       // "123"   -> 123
toString       // 123     -> "123"
toBoolean      // any     -> Boolean(value)

// arithmetic (decimal.js precision, "<op>:<arg>" form)
multiply:100   // 1.5     -> 150        (major -> minor units)
divide:100     // 150     -> 1.5        (minor -> major units)
round:2        // 1.235   -> 1.24       (half-up)
floor:0        // 1.9     -> 1          (truncate to N decimals)
ceil:0         // 1.1     -> 2`}
              />

              <HelpSection
                title={t(KEYS.help.success_condition_title)}
                description={t(KEYS.help.success_condition_description)}
                code={`// Evaluated against response.data
"successCondition": "$.success === 'true' && $.result"
"successCondition": "$.status !== 'error' || $.data.id"
"successCondition": "$.data.transactionId"   // truthy check`}
              />

              <HelpSection
                title={t(KEYS.help.status_mappings_title)}
                description={t(KEYS.help.status_mappings_description)}
                code={`"statusMappings": {
  "field":   "$.result.status",
  "primary": {
    "auto_success": "completed",
    "hand_success": "completed",
    "auto_decline": "cancelled",
    "cancelled":    "cancelled",
    "chargeback":   "error"
  },
  "subStatusField": "$.result.subStatus",  // optional, takes precedence
  "subStatus":      { "review": "dispute" },
  "default":        "customer_confirm"
}`}
              />

              <HelpSection
                title={t(KEYS.help.mappings_title)}
                description={t(KEYS.help.mappings_description)}
                code={`// Source: appConfig — read from AppConfig BanksMap / PaymentTypesMap
"mappings": {
  "source": "appConfig",
  "banksOverrides": {
    "toProvider": { "sberbank": "SBER" },
    "toInternal": { "SBER": "sberbank" }
  }
}

// Source: inline — embed tables directly
"mappings": {
  "source": "inline",
  "paymentTypes": {
    "toProvider": { "card": "CARD", "sbp": "SBP" },
    "toInternal": { "CARD": "card", "SBP": "sbp" }
  },
  "banks": { "toProvider": { ... }, "toInternal": { ... } }
}

// Use in TransformRule:
{ "target": "methodTypeId", "source": "$.createOrderDto.payment.type", "mapping": "paymentTypes.toProvider" }`}
              />

              <HelpSection
                title={t(KEYS.help.webhook_title)}
                description={t(KEYS.help.webhook_description)}
                code={`"webhook": {
  "orderIdField": "$.foreignId",      // path inside incoming webhook body
  "statusField":  "$.status",
  "amountField":  "$.amount",
  "auth": {
    "type": "none"                    // or "signature" | "header_token"
  },
  "responseTransform": []             // optional rewrite of the webhook payload
}

// Signature over incoming X-Timestamp + raw body, with freshness check (e.g. Paydraw)
"auth": {
  "type": "signature",
  "signature": {
    "algorithm":       "hmac-sha256",    // key = text before the first ":"
    "template":        "\${credentials.webhookSecret}:\${headers.X-Timestamp}.\${requestBodyJson}",
    "encoding":        "hex",            // hex | base64
    "signatureHeader": "X-Signature",    // or "signatureField": "$.sign" (inside the body)
    "timestamp": {                       // optional: reject stale / future webhooks
      "header":           "X-Timestamp", // epoch s | epoch ms | ISO 8601
      "toleranceSeconds": 300            // |receivedAt - X-Timestamp| <= 300 s
    }
  }
}

// Timestamp and signature in one header, X-Velora-Signature: t=<unix s>,v1=<hex> (e.g. Velora)
"auth": {
  "type": "signature",
  "signature": {
    "algorithm":            "hmac-sha256",
    "template":             "\${credentials.webhookSecret}:\${headerParams.X-Velora-Signature.t}.\${requestBodyJson}",
    "encoding":             "hex",
    "signatureHeader":      "X-Velora-Signature",
    "signatureHeaderParam": "v1",       // signature = v1 pair; any of repeated v1 may match
    "timestamp": {
      "header":           "X-Velora-Signature",
      "param":            "t",          // timestamp = t pair
      "toleranceSeconds": 300
    }
  }
}

// Signature template context:
//   credentials      provider credentials (payin or payout side)
//   headers          incoming headers, name case ignored: \${headers.x-timestamp} == \${headers.X-Timestamp}
//   headerParams     key=value pairs of a header, only with timestamp.param / signatureHeaderParam:
//                    \${headerParams.X-Velora-Signature.t}
//   requestBody      parsed body
//   requestBodyJson  raw body exactly as received (no re-serialization)
// \${timestamp} / \${nonce} belong to outgoing requests and are NOT available here.`}
              />

              <HelpSection
                title={t(KEYS.help.required_credentials_title)}
                description={t(KEYS.help.required_credentials_description)}
                code={`"requiredCredentials": ["apiKey", "secretKey", "fernetKey"]
// Checked by isValidConfig — adapter refuses to run if any field is missing
// in provider.payinConfig.credentials / payoutConfig.credentials.`}
              />

              <HelpSection
                title={t(KEYS.help.complete_example_title)}
                description={t(KEYS.help.complete_example_description)}
                code={`{
  "name": "myprovider-dynamic",
  "providerType": "myprovider",
  "baseUrl": "\${provider.payinConfig.url}",
  "payin": {
    "endpoints": {
      "create": {
        "path": "/api/v2/order/init",
        "method": "POST",
        "requestTransform": [
          { "target": "partnerId",  "source": "$.credentials.partnerId" },
          { "target": "externalId", "source": "$.order.id" },
          { "target": "amount",     "source": "$.createOrderDto.amount" },
          { "target": "type",       "source": "$.createOrderDto.payment.type",
            "mapping": "paymentTypes.toProvider" }
        ],
        "responseTransform": [
          { "target": "externalOrderId", "source": "$.response.data.transactionId",
            "transform": "toString" },
          { "target": "status", "source": "$.response.data.status",
            "mapping": "statusMappings.primary" },
          { "target": "requisites.bank", "source": "$.response.data.requisites.bank",
            "mapping": "banks.toInternal" },
          { "target": "requisites.cardholder", "source": "$.response.data.requisites.name" }
        ],
        "successCondition": "$.status === 'success' && $.data.transactionId"
      },
      "get": {
        "path": "/api/v2/order/status",
        "method": "POST",
        "requestTransform": [
          { "target": "partnerId",     "source": "$.credentials.partnerId" },
          { "target": "transactionId", "source": "$.externalOrderId", "transform": "toNumber" }
        ],
        "responseTransform": [
          { "target": "status", "source": "$.response.data.status",
            "mapping": "statusMappings.primary" }
        ],
        "successCondition": "$.status === 'success'"
      }
    },
    "auth": {
      "type": "header",
      "headers": [
        { "name": "Content-Type",  "value": "application/json" },
        { "name": "Authorization", "value": "\${credentials.apiKey}" }
      ]
    },
    "statusMappings": {
      "field":   "$.data.status",
      "primary": {
        "CREATED":   "customer_confirm",
        "SUCCESS":   "completed",
        "CANCELLED": "cancelled",
        "ERROR":     "error"
      },
      "default": "customer_confirm"
    },
    "mappings": { "source": "appConfig" },
    "webhook": {
      "orderIdField": "$.transactionId",
      "statusField":  "$.status",
      "amountField":  "$.amount",
      "auth": { "type": "none" },
      "responseTransform": []
    },
    "requiredCredentials": ["partnerId", "apiKey"]
  }
}`}
              />
            </Stack>
          )}

          {/* Validation Results */}
          <Collapse in={!!validationResult}>
            <Box sx={{ mt: 2 }}>
              {validationResult && (
                <Stack spacing={1}>
                  {validationResult.valid ? (
                    <Alert severity="success" icon={<Check />}>
                      {t(KEYS.validation.success)}
                    </Alert>
                  ) : (
                    <Alert severity="error" icon={<Warning />}>
                      {t(KEYS.validation.errors)}
                      <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                        {validationResult.errors.map((err, idx) => (
                          <li key={idx}>
                            <strong>
                              {err.path}
                              {': '}
                            </strong>
                            {err.message}
                          </li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                  {validationResult.warnings.length > 0 && (
                    <Alert severity="warning">
                      {t(KEYS.validation.warnings)}
                      <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                        {validationResult.warnings.map((warning, idx) => (
                          <li key={idx}>{warning}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                </Stack>
              )}
            </Box>
          </Collapse>
        </form>
      </DialogContent>

      <DialogActions sx={{ px: 6, py: 3 }}>
        <Button
          variant="outlined"
          startIcon={
            validateJsonMutation.isLoading ? (
              <CircularProgress size={16} />
            ) : (
              <PlayArrow />
            )
          }
          onClick={handleValidate}
          disabled={validateJsonMutation.isLoading}
        >
          {t(KEYS.actions.validate)}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleClose}>{tCommon('buttons.cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => formik.handleSubmit()}
          disabled={!formik.isValid || formik.isSubmitting}
        >
          {tCommon('buttons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
